// apps/client/src/pages/leads/consent/ConsentTextsPage.tsx
//
// Lead Capture > Consent (/leads/consent), super_admin only.
//
// The exact wording people agree to on your website forms lives here,
// versioned. For each purpose: the active version, drafts, and history.
// An active text never changes: new wording is a new draft, and activating
// it retires the previous active text of that purpose in one step. Every
// consent record points at the version that was shown, so the proof of
// what someone agreed to survives later edits.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.3, §5 and §9.

import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertTriangle, Archive, CheckCircle2, ChevronDown, ChevronRight, FilePlus2, FileText, ScrollText } from 'lucide-react';
import {
  CONSENT_PURPOSES,
  CONSENT_PURPOSE_LABELS,
  CONSENT_TEXT_MAX_LENGTH,
  type ConsentPurpose,
} from '@valutten/shared-schemas/dist/consent';
import { WEBSITE_FORM_CONSENT_PURPOSES } from '@valutten/shared-schemas/dist/websiteForms';
import { PageHeader } from '../../../components/common/PageHeader';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { Spinner } from '../../../components/common/Spinner';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { timestampToMillis } from '../../../hooks/useWebsiteConnections';
import { groupConsentTexts, useConsentTexts, type ConsentTextView } from '../../../hooks/useConsentTexts';
import { FORM_LABELS } from '../website/websiteConnectionForm';

type OutletCtx = { userData?: { companyId?: string; role?: string } };

const PURPOSE_HELP: Readonly<Record<ConsentPurpose, string>> = {
  enquiry_contact: 'Permission to contact the person about the enquiry they sent.',
  marketing_email: 'Permission to send marketing emails. Checked before any newsletter, campaign or sequence email is sent.',
  referral_third_party: 'The referrer confirms the client agreed to be referred and to have their details passed on.',
  referrer_terms: 'The referrer accepts your referral terms.',
};

/** Where each purpose appears, from the public forms contract. */
export function purposeUsage(purpose: ConsentPurpose): string {
  const uses = (Object.keys(WEBSITE_FORM_CONSENT_PURPOSES) as Array<keyof typeof WEBSITE_FORM_CONSENT_PURPOSES>)
    .flatMap((form) =>
      WEBSITE_FORM_CONSENT_PURPOSES[form]
        .filter((p) => p.purpose === purpose)
        .map((p) => `${FORM_LABELS[form]} form (${p.required ? 'required' : 'optional'})`),
    );
  return uses.length > 0 ? `Shown on: ${uses.join(', ')}.` : 'Not shown on a website form.';
}

function formatDate(value: unknown): string {
  const ms = timestampToMillis(value);
  if (ms === null) return '';
  return new Date(ms).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric' });
}

type Pending = { kind: 'activate' | 'retire'; text: ConsentTextView; hadActive: boolean } | null;

const TextBlock: React.FC<{ text: ConsentTextView; meta: string }> = ({ text, meta }) => (
  <div>
    {text.label && <p className="text-xs font-semibold text-gray-300 mb-1">{text.label}</p>}
    <p className="text-sm whitespace-pre-wrap break-words">{text.text}</p>
    <p className="text-[0.6875rem] text-gray-500 mt-1">
      {meta} · <span className="font-mono">{text.versionId}</span> · fingerprint <span className="font-mono">{text.sha256.slice(0, 12)}</span>
    </p>
  </div>
);

const DraftForm: React.FC<{
  purpose: ConsentPurpose;
  initialText: string;
  onCancel: () => void;
  onSave: (input: { purpose: ConsentPurpose; text: string; label: string }) => Promise<void>;
}> = ({ purpose, initialText, onCancel, onSave }) => {
  const [text, setText] = useState(initialText);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = text.trim();

  const save = async () => {
    if (!trimmed) {
      setError('Enter the wording people will see.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ purpose, text: trimmed, label: label.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The draft was not saved.');
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 space-y-3">
      <div>
        <label htmlFor={`draft-label-${purpose}`} className="block text-xs font-semibold text-gray-400 mb-1">Internal name (optional)</label>
        <input id={`draft-label-${purpose}`} type="text" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="For example: Privacy policy v2.6 wording"
          className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-sm" />
      </div>
      <div>
        <label htmlFor={`draft-text-${purpose}`} className="block text-xs font-semibold text-gray-400 mb-1">Wording shown next to the tick box</label>
        <textarea id={`draft-text-${purpose}`} rows={4} maxLength={CONSENT_TEXT_MAX_LENGTH} value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-sm" />
        <p className="text-[0.6875rem] text-gray-500 text-right">{text.length.toLocaleString('en-AU')} / {CONSENT_TEXT_MAX_LENGTH.toLocaleString('en-AU')}</p>
      </div>
      {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-sm">Cancel</button>
        <button type="button" onClick={save} disabled={saving}
          className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {saving && <Spinner size="sm" />}
          Save draft
        </button>
      </div>
    </div>
  );
};

const ConsentTextsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx.userData?.companyId;
  const isSuperAdmin = ctx.userData?.role === 'super_admin';
  const { texts, loading, error, refresh, createDraft, activate, retire } = useConsentTexts(isSuperAdmin ? companyId : undefined);

  const groups = useMemo(() => groupConsentTexts(texts, CONSENT_PURPOSES), [texts]);
  const [drafting, setDrafting] = useState<ConsentPurpose | null>(null);
  const [historyOpen, setHistoryOpen] = useState<Set<ConsentPurpose>>(new Set());
  const [pending, setPending] = useState<Pending>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const runPending = async () => {
    if (!pending) return;
    const { kind, text } = pending;
    setPending(null);
    setBusyId(text.versionId);
    setActionError(null);
    setNotice(null);
    try {
      if (kind === 'activate') {
        await activate(text.versionId);
        setNotice(`New ${CONSENT_PURPOSE_LABELS[text.purpose].toLowerCase()} wording is live on your website forms.`);
      } else {
        await retire(text.versionId);
        setNotice(text.status === 'draft' ? 'Draft discarded.' : 'Wording retired.');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const toggleHistory = (p: ConsentPurpose) =>
    setHistoryOpen((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  if (!isSuperAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader icon={ScrollText} title="Consent wording" />
        <div className="min-h-[50vh] rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-300 text-sm">
          Only a super admin can manage consent wording.
        </div>
      </div>
    );
  }

  const confirmMessage = (() => {
    if (!pending) return '';
    if (pending.kind === 'activate') {
      return (
        'Once active, this wording cannot be edited. Website forms start showing it straight away, and every consent ' +
        'given from now on is recorded against this exact version. To change it later you create a new draft and activate that.' +
        (pending.hadActive ? ' The wording active now is retired; records already made against it are kept.' : '')
      );
    }
    if (pending.text.status === 'draft') return 'This draft is retired and can no longer be activated.';
    return (
      'Website forms that need this consent stop working until you activate new wording for it. ' +
      'Records already made against this version are kept.'
    );
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ScrollText}
        title="Consent wording"
        subtitle="The wording people agree to on your website forms. Each version is kept, so you can always show exactly what someone agreed to."
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {error && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {actionError && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">{notice}</div>
      )}

      {loading && texts.length === 0 ? (
        <div className="space-y-4" aria-label="Loading consent wording">
          <Skeleton height="9rem" />
          <Skeleton height="9rem" />
        </div>
      ) : (
        <div className="space-y-4">
          {CONSENT_PURPOSES.map((purpose) => {
            const g = groups[purpose];
            const showHistory = historyOpen.has(purpose);
            return (
              <section key={purpose} aria-label={CONSENT_PURPOSE_LABELS[purpose]}
                className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-700 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold">{CONSENT_PURPOSE_LABELS[purpose]}</h2>
                    <p className="text-xs text-gray-400 mt-0.5">{PURPOSE_HELP[purpose]}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{purposeUsage(purpose)}</p>
                  </div>
                  {drafting !== purpose && (
                    <button type="button" onClick={() => setDrafting(purpose)}
                      className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-sm inline-flex items-center gap-2 self-start">
                      <FilePlus2 className="w-4 h-4" />
                      New draft
                    </button>
                  )}
                </div>

                <div className="p-5 space-y-4">
                  <div>
                    <p className="text-[0.6875rem] uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Active
                    </p>
                    {g.active ? (
                      <div className="flex flex-col md:flex-row md:items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <TextBlock text={g.active} meta={`Active since ${formatDate(g.active.activatedAt) || 'unknown'}`} />
                        </div>
                        <button type="button" disabled={busyId === g.active.versionId}
                          onClick={() => setPending({ kind: 'retire', text: g.active!, hadActive: true })}
                          className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-red-700 text-xs inline-flex items-center gap-1 self-start disabled:opacity-50">
                          <Archive className="w-3.5 h-3.5" /> Retire
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-amber-300">
                        No active wording. Website forms that need this consent are not offered until you activate a draft.
                      </p>
                    )}
                  </div>

                  {drafting === purpose && (
                    <DraftForm
                      purpose={purpose}
                      initialText={g.active?.text ?? ''}
                      onCancel={() => setDrafting(null)}
                      onSave={async (input) => {
                        await createDraft(input);
                        setDrafting(null);
                        setNotice('Draft saved. Activate it when it is ready to go live.');
                      }}
                    />
                  )}

                  {g.drafts.length > 0 && (
                    <div>
                      <p className="text-[0.6875rem] uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" /> Drafts
                      </p>
                      <ul className="space-y-3">
                        {g.drafts.map((d) => (
                          <li key={d.versionId} className="rounded-md border border-gray-700 p-3 flex flex-col md:flex-row md:items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <TextBlock text={d} meta={`Drafted ${formatDate(d.createdAt)}`} />
                            </div>
                            <div className="flex gap-2 self-start">
                              <button type="button" disabled={busyId === d.versionId}
                                onClick={() => setPending({ kind: 'activate', text: d, hadActive: g.active !== null })}
                                className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50">
                                {busyId === d.versionId ? <Spinner size="sm" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                Activate
                              </button>
                              <button type="button" disabled={busyId === d.versionId}
                                onClick={() => setPending({ kind: 'retire', text: d, hadActive: g.active !== null })}
                                className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-xs inline-flex items-center gap-1 disabled:opacity-50">
                                <Archive className="w-3.5 h-3.5" /> Discard
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {g.history.length > 0 && (
                    <div>
                      <button type="button" onClick={() => toggleHistory(purpose)} aria-expanded={showHistory}
                        className="text-[0.6875rem] uppercase tracking-wider text-gray-400 hover:text-gray-200 flex items-center gap-1">
                        {showHistory ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        History ({g.history.length})
                      </button>
                      {showHistory && (
                        <ul className="mt-2 space-y-3">
                          {g.history.map((h) => (
                            <li key={h.versionId} className="rounded-md border border-gray-700/60 p-3 opacity-80">
                              <TextBlock
                                text={h}
                                meta={
                                  h.status === 'active'
                                    ? `Also marked active since ${formatDate(h.activatedAt) || 'unknown'}`
                                    : h.activatedAt
                                      ? `Active ${formatDate(h.activatedAt)} to ${formatDate(h.retiredAt) || 'unknown'}`
                                      : `Discarded draft, ${formatDate(h.retiredAt) || formatDate(h.createdAt)}`
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={pending !== null}
        title={
          pending?.kind === 'activate'
            ? 'Activate this wording?'
            : pending?.text.status === 'draft'
              ? 'Discard this draft?'
              : 'Retire the active wording?'
        }
        message={confirmMessage}
        confirmLabel={pending?.kind === 'activate' ? 'Activate' : pending?.text.status === 'draft' ? 'Discard' : 'Retire'}
        confirmVariant={pending?.kind === 'activate' ? 'primary' : 'danger'}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default ConsentTextsPage;
